import { useRef, useState } from 'react';

/**
 * Swipe a list row to act on it.
 *
 * The same gesture the forms site uses on its approval list, deliberately:
 * agents move between the two tools all day, and a drag that commits at a
 * different distance or snaps back differently is a small tax paid on every
 * interaction. The two tuning numbers are copied rather than re-derived.
 *
 *   THRESHOLD   how far the row must travel before release commits
 *   DAMPEN      softens the translate past the threshold, so dragging
 *               further feels bounded rather than unlimited
 *
 * THE DEAD ZONE AND THE CAPTURE ARE THE WHOLE TRICK. A row here is also a
 * click target (open the peek) and a right-click target (the menu), so the
 * gesture must not steal an ordinary click. Nothing moves for the first six
 * pixels; the pointer is captured only at eight, once a drag is genuinely
 * underway. Before that the browser's own click handling is untouched.
 *
 * ONE HOOK FOR THE WHOLE TABLE, not one per row: only one row can be dragged
 * at a time, and a hook cannot be called inside the row loop anyway. Each row
 * asks `row()` — a plain function, safe in a map — for its own state.
 */
const THRESHOLD = 120;
const DAMPEN = 0.35;
const DEAD_ZONE = 6;
const CAPTURE_AT = 8;

export interface SwipeAction {
  /** Shown while dragging. Name the outcome, not the gesture. */
  label: string;
  /** False when the action would do nothing — no tint, no travel, no commit. */
  enabled: boolean;
  run: () => void;
}

/**
 * Where a drag of `raw` pixels has got to — the whole gesture, as arithmetic.
 *
 * Pulled out of the hook so the tuning can be tested without a DOM: the dead
 * zone, the refusal to travel in a disallowed direction, the commit
 * threshold and the damping past it are the parts that decide how this
 * feels, and they are the parts most likely to be broken by a later tweak.
 */
export function swipeGeometry(
  raw: number,
  left: SwipeAction,
  right: SwipeAction,
): Omit<RowSwipeState, 'handlers'> {
  const side: 'left' | 'right' | null =
    raw < -DEAD_ZONE ? 'left' : raw > DEAD_ZONE ? 'right' : null;
  const candidate = side === 'left' ? left : side === 'right' ? right : null;
  const action = candidate?.enabled ? candidate : null;

  // A disallowed direction does not move at all, which is how the row says
  // "not that way" without needing a message.
  const travel = action ? raw : 0;
  const over = Math.max(0, Math.abs(travel) - THRESHOLD);

  return {
    action,
    side: action ? side : null,
    past: Math.abs(travel) >= THRESHOLD,
    progress: Math.min(1, Math.abs(travel) / THRESHOLD),
    translateX:
      travel === 0
        ? 0
        : Math.sign(travel) * (Math.min(Math.abs(travel), THRESHOLD) + over * DAMPEN),
  };
}

export interface RowSwipeState {
  /** Which action a release would commit right now, if any. */
  action: SwipeAction | null;
  side: 'left' | 'right' | null;
  past: boolean;
  /** 0..1 towards committing, for ramping a tint. */
  progress: number;
  /** Translate to apply to the row, damped past the threshold. */
  translateX: number;
  handlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: () => void;
  };
}


/**
 * Stop the browser selecting text while a row is being dragged.
 *
 * A pointer drag across a row is, to the browser, an ordinary text
 * selection: by the time the row has travelled far enough to commit, the
 * subject, the requester and half the next row are highlighted blue, and the
 * highlight stays after the gesture ends. It made a deliberate action look
 * like a mis-click.
 *
 * Applied to the document rather than the row because the pointer leaves the
 * row — that is what a drag IS — and a `user-select: none` scoped to the row
 * stops nothing once the selection has escaped it. The existing selection is
 * cleared as well, since the first few pixels happen before the gesture is
 * recognised as a drag.
 */
function suppressSelection(on: boolean): void {
  if (typeof document === 'undefined') return;
  const style = document.body?.style;
  if (!style) return;
  if (on) {
    style.userSelect = 'none';
    // Safari and older WebKit still want the prefix.
    style.setProperty('-webkit-user-select', 'none');
    try {
      document.getSelection()?.removeAllRanges();
    } catch {
      /* a selection we cannot clear is not worth failing the drag over */
    }
  } else {
    style.userSelect = '';
    style.removeProperty('-webkit-user-select');
  }
}

export function useRowSwipe() {
  const [id, setId] = useState<string | null>(null);
  const [dx, setDx] = useState(0);
  const startX = useRef(0);
  const moved = useRef(false);

  return {
    /**
     * Did the last pointer sequence become a drag?
     *
     * The click that follows a drag has to be ignored, or every swipe also
     * opens the ticket. Cleared on the next pointer-down rather than on
     * pointer-up, because the click arrives after the up.
     */
    swallowedClick: () => moved.current,

    row(rowId: string, left: SwipeAction, right: SwipeAction): RowSwipeState {
      const mine = id === rowId;
      const g = swipeGeometry(mine ? dx : 0, left, right);
      const { action, past } = g;

      return {
        ...g,
        handlers: {
          onPointerDown(e) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            // Anything interactive keeps its own pointer events. The row's own
            // links are fair game — they cover most of its surface, and a
            // gesture that worked only in the gaps would never be found.
            if (
              (e.target as Element)?.closest?.(
                '[data-noswipe="true"], button, a, input, select, textarea, [role="menuitem"]',
              )
            ) {
              return;
            }
            startX.current = e.clientX;
            moved.current = false;
            setId(rowId);
            setDx(0);
          },
          onPointerMove(e) {
            if (!mine) return;
            const next = e.clientX - startX.current;
            if (!moved.current && Math.abs(next) > CAPTURE_AT) {
              // Only now: capturing earlier would swallow any click whose
              // pointer wandered a pixel or two, which is most clicks.
              try {
                (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
              } catch {
                /* capture is an optimisation, not a requirement */
              }
              moved.current = true;
              // Only once it is genuinely a drag — a click that wandered two
              // pixels must not disturb the page's selection behaviour.
              suppressSelection(true);
            }
            setDx(next);
          },
          onPointerUp(e) {
            if (!mine) return;
            setId(null);
            setDx(0);
            try {
              (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
            } catch {
              /* noop */
            }
            suppressSelection(false);
            if (past && action) action.run();
          },
          onPointerCancel() {
            setId(null);
            setDx(0);
            suppressSelection(false);
          },
        },
      };
    },
  };
}
